/**
 * Pure service functions for holding/event/loan state mutations.
 * Each function takes current state arrays, performs ALL mutations in one pass,
 * and returns new state arrays. No React state calls — no race conditions.
 */

import { Holding, LiquidityEvent, Loan, HoldingHistoryEntry } from '../types'

// ─── Helpers ────────────────────────────────────────────────────────────────

function genId(): string {
  return Math.random().toString(36).substr(2, 9)
}

function withHistoryEntry(
  holding: Holding,
  newAmount: number,
  now: string,
): Holding {
  const currentHistory: HoldingHistoryEntry[] = holding.history || [
    { amount: holding.amount, date: holding.createdAt || now },
  ]
  return {
    ...holding,
    amount: newAmount,
    history: [...currentHistory, { amount: newAmount, date: now }],
  }
}

/** Collect all event IDs associated with a real estate holding */
function collectLinkedEventIds(
  holding: Holding,
  loans: Loan[],
  events: LiquidityEvent[],
): Set<string> {
  const ids = new Set<string>()

  if (holding.autoCreatedEventIds) {
    holding.autoCreatedEventIds.forEach((eid) => ids.add(eid))
  }
  if (holding.linkedEventIds) {
    holding.linkedEventIds.forEach((eid) => ids.add(eid))
  }
  if (holding.linkedLoanId) {
    const loan = loans.find((l) => l.id === holding.linkedLoanId)
    if (loan) {
      events.forEach((e) => {
        if (e.name.startsWith(`${loan.name} Deposit`)) {
          ids.add(e.id)
        }
      })
    }
  }
  // Fallback: match by name pattern for older holdings
  events.forEach((e) => {
    if (
      e.name.startsWith(`${holding.name} Rental Income`) ||
      e.name.startsWith(`${holding.name} Closing Costs`)
    ) {
      ids.add(e.id)
    }
  })

  return ids
}

/** Find events that represent funded costs (negative one-time events with a funding source) */
function findFundedCostEvents(
  eventIds: Set<string>,
  events: LiquidityEvent[],
): LiquidityEvent[] {
  return events.filter(
    (e) =>
      eventIds.has(e.id) &&
      e.fundedFromHoldingId &&
      e.startYear === 0 &&
      e.amount < 0,
  )
}

// ─── Toggle Holding Active ──────────────────────────────────────────────────

export interface ToggleResult {
  holdings: Holding[]
  events: LiquidityEvent[]
  loans: Loan[]
}

export function toggleHoldingActive(
  holdings: Holding[],
  events: LiquidityEvent[],
  loans: Loan[],
  holdingId: string,
): ToggleResult {
  const holding = holdings.find((h) => h.id === holdingId)
  if (!holding) return { holdings, events, loans }

  const now = new Date().toISOString()
  const newActive = !(holding.isActive ?? true)

  let newHoldings = holdings.map((h) =>
    h.id === holdingId ? { ...h, isActive: newActive } : h,
  )
  let newEvents = [...events]
  let newLoans = [...loans]

  if (holding.isRealEstate) {
    const linkedEventIds = collectLinkedEventIds(holding, loans, events)

    // Toggle all linked events
    newEvents = newEvents.map((e) =>
      linkedEventIds.has(e.id) ? { ...e, isActive: newActive } : e,
    )

    // Toggle linked loan
    if (holding.linkedLoanId) {
      newLoans = newLoans.map((l) =>
        l.id === holding.linkedLoanId ? { ...l, isActive: newActive } : l,
      )
    }

    // Handle funding source balance changes
    const fundedEvents = findFundedCostEvents(linkedEventIds, events)
    for (const evt of fundedEvents) {
      const amount = Math.abs(evt.amount)
      const fundingId = evt.fundedFromHoldingId!
      const isCurrentlyDeducted = evt.deductionApplied ?? true // assume applied for legacy events

      if (!newActive && isCurrentlyDeducted) {
        // Deactivating and deduction is applied → restore funds
        newHoldings = newHoldings.map((h) => {
          if (h.id !== fundingId) return h
          return withHistoryEntry(h, h.amount + amount, now)
        })
        // Mark deduction as not applied
        newEvents = newEvents.map((e) =>
          e.id === evt.id ? { ...e, deductionApplied: false } : e,
        )
      } else if (newActive && !isCurrentlyDeducted) {
        // Reactivating and deduction is not applied → re-deduct
        newHoldings = newHoldings.map((h) => {
          if (h.id !== fundingId) return h
          return withHistoryEntry(h, Math.max(0, h.amount - amount), now)
        })
        // Mark deduction as applied
        newEvents = newEvents.map((e) =>
          e.id === evt.id ? { ...e, deductionApplied: true } : e,
        )
      }
    }
  }

  return { holdings: newHoldings, events: newEvents, loans: newLoans }
}

// ─── Delete Holding ─────────────────────────────────────────────────────────

export interface DeleteResult {
  holdings: Holding[]
  events: LiquidityEvent[]
  loans: Loan[]
}

export function deleteHolding(
  holdings: Holding[],
  events: LiquidityEvent[],
  loans: Loan[],
  holdingId: string,
): DeleteResult {
  const holding = holdings.find((h) => h.id === holdingId)
  if (!holding) return { holdings, events, loans }

  const now = new Date().toISOString()
  let newHoldings = [...holdings]
  let newEvents = [...events]
  let newLoans = [...loans]

  if (holding.isRealEstate) {
    const linkedEventIds = collectLinkedEventIds(holding, loans, events)

    // Restore funding source balances only if deduction is currently applied
    const fundedEvents = findFundedCostEvents(linkedEventIds, events)
    for (const evt of fundedEvents) {
      const isCurrentlyDeducted =
        evt.deductionApplied ?? holding.isActive ?? true
      if (isCurrentlyDeducted) {
        const restoreAmount = Math.abs(evt.amount)
        const fundingId = evt.fundedFromHoldingId!
        newHoldings = newHoldings.map((h) => {
          if (h.id !== fundingId) return h
          return withHistoryEntry(h, h.amount + restoreAmount, now)
        })
      }
    }

    // Delete linked loan
    if (holding.linkedLoanId) {
      newLoans = newLoans.filter((l) => l.id !== holding.linkedLoanId)
    }

    // Delete linked events
    newEvents = newEvents.filter((e) => !linkedEventIds.has(e.id))
  }

  newHoldings = newHoldings.filter((h) => h.id !== holdingId)

  return { holdings: newHoldings, events: newEvents, loans: newLoans }
}

// ─── Add Real Estate Holding ────────────────────────────────────────────────

export interface AddRealEstateResult {
  holdings: Holding[]
  events: LiquidityEvent[]
}

export function addRealEstateHolding(
  holdings: Holding[],
  events: LiquidityEvent[],
  newHolding: Omit<Holding, 'id' | 'createdAt' | 'history'>,
): AddRealEstateResult {
  const now = new Date().toISOString()
  const holdingId = genId()

  let newHoldings = [...holdings]
  let newEvents = [...events]
  const autoCreatedEventIds: string[] = []

  const hasLinkedIncomeEvent =
    newHolding.linkedEventIds && newHolding.linkedEventIds.length > 0
  if (
    !hasLinkedIncomeEvent &&
    newHolding.rentalIncome &&
    newHolding.rentalIncome > 0
  ) {
    const eventId = genId()
    autoCreatedEventIds.push(eventId)
    const rentalEvent: LiquidityEvent = {
      id: eventId,
      name: `${newHolding.name} Rental Income`,
      amount: newHolding.rentalIncome,
      currency: newHolding.currency,
      type: 'monthly',
      startYear: 0,
      isActive: true,
    }
    // If the property is set to sell, cap rental income at the sale year
    if (
      newHolding.realEstateStrategy === 'sell' &&
      newHolding.plannedSaleYear != null
    ) {
      rentalEvent.endYear = newHolding.plannedSaleYear
    }
    newEvents.push(rentalEvent)
  }

  // Auto-create sale proceeds event if strategy is 'sell'
  if (
    newHolding.realEstateStrategy === 'sell' &&
    newHolding.plannedSaleYear != null
  ) {
    const saleEventId = genId()
    autoCreatedEventIds.push(saleEventId)
    // Calculate projected appreciated value at sale year
    const appreciationRate =
      newHolding.appreciationRate ?? newHolding.expectedReturn ?? 0
    const appreciatedValue =
      newHolding.amount *
      Math.pow(1 + appreciationRate / 100, newHolding.plannedSaleYear)
    newEvents.push({
      id: saleEventId,
      name: `${newHolding.name} Sale Proceeds`,
      amount: Math.round(appreciatedValue),
      currency: newHolding.currency,
      type: 'one-time',
      startYear: newHolding.plannedSaleYear,
      isActive: true,
      displayOnly: true,
      displayOnlyMeta: {
        sourceHolding: newHolding.name,
        currentValue: newHolding.amount,
        appreciationRate,
        mechanism:
          "Processed by the projection engine's illiquid bucket system. The property appreciates monthly until the sale year, then its full value enters the drawdown pool as non-yielding cash.",
      },
    })
  }

  if (newHolding.closingCosts && newHolding.closingCosts > 0) {
    const eventId = genId()
    autoCreatedEventIds.push(eventId)
    const fundingHolding = newHolding.closingCostsFundedFromHoldingId
      ? holdings.find(
          (h) => h.id === newHolding.closingCostsFundedFromHoldingId,
        )
      : null

    const willDeduct = !!newHolding.closingCostsFundedFromHoldingId

    newEvents.push({
      id: eventId,
      name: `${newHolding.name} Closing Costs${fundingHolding ? ` (from ${fundingHolding.name})` : ''}`,
      amount: -newHolding.closingCosts,
      currency: newHolding.currency,
      type: 'one-time',
      startYear: 0,
      isActive: true,
      fundedFromHoldingId:
        newHolding.closingCostsFundedFromHoldingId || undefined,
      deductionApplied: willDeduct, // mark that we're about to deduct
    })

    if (newHolding.closingCostsFundedFromHoldingId) {
      newHoldings = newHoldings.map((h) => {
        if (h.id !== newHolding.closingCostsFundedFromHoldingId) return h
        return withHistoryEntry(
          h,
          Math.max(0, h.amount - newHolding.closingCosts!),
          now,
        )
      })
    }
  }

  const holding: Holding = {
    ...newHolding,
    id: holdingId,
    createdAt: now,
    autoCreatedEventIds,
    history: [{ amount: newHolding.amount, date: now }],
  }
  newHoldings.push(holding)

  return { holdings: newHoldings, events: newEvents }
}

// ─── Add Loan (with optional deposit deduction) ────────────────────────────

export interface AddLoanResult {
  holdings: Holding[]
  events: LiquidityEvent[]
  loans: Loan[]
  loanId: string
}

export function addLoanWithDeposit(
  holdings: Holding[],
  events: LiquidityEvent[],
  loans: Loan[],
  newLoan: Omit<Loan, 'id' | 'createdAt'>,
): AddLoanResult {
  const now = new Date().toISOString()
  const loanId = genId()

  const loan: Loan = {
    ...newLoan,
    id: loanId,
    createdAt: now,
  }

  let newHoldings = [...holdings]
  let newEvents = [...events]
  const newLoans = [...loans, loan]

  if (newLoan.depositFundedFromHoldingId && newLoan.depositAmount > 0) {
    const fundingHolding = holdings.find(
      (h) => h.id === newLoan.depositFundedFromHoldingId,
    )
    const eventId = genId()
    const startYear = newLoan.startYear || 0
    const willDeductNow = startYear === 0

    newEvents.push({
      id: eventId,
      name: `${newLoan.name} Deposit${fundingHolding ? ` (from ${fundingHolding.name})` : ''}`,
      amount: -newLoan.depositAmount,
      currency: newLoan.currency,
      type: 'one-time',
      startYear,
      isActive: true,
      fundedFromHoldingId: newLoan.depositFundedFromHoldingId,
      deductionApplied: willDeductNow, // only applied if starts now
    })

    if (willDeductNow) {
      newHoldings = newHoldings.map((h) => {
        if (h.id !== newLoan.depositFundedFromHoldingId) return h
        return withHistoryEntry(
          h,
          Math.max(0, h.amount - newLoan.depositAmount),
          now,
        )
      })
    }
  }

  return { holdings: newHoldings, events: newEvents, loans: newLoans, loanId }
}

// ─── Delete Event (with optional funding source restoration) ────────────────

export interface DeleteEventResult {
  holdings: Holding[]
  events: LiquidityEvent[]
}

export function deleteEvent(
  holdings: Holding[],
  events: LiquidityEvent[],
  eventId: string,
): DeleteEventResult {
  const event = events.find((e) => e.id === eventId)
  let newHoldings = [...holdings]

  // Restore funding source only if deduction is currently applied
  if (
    event?.fundedFromHoldingId &&
    event.startYear === 0 &&
    event.amount < 0 &&
    (event.deductionApplied ?? true) // assume applied for legacy events
  ) {
    const restoreAmount = Math.abs(event.amount)
    const now = new Date().toISOString()
    newHoldings = newHoldings.map((h) => {
      if (h.id !== event.fundedFromHoldingId) return h
      return withHistoryEntry(h, h.amount + restoreAmount, now)
    })
  }

  const newEvents = events.filter((e) => e.id !== eventId)
  return { holdings: newHoldings, events: newEvents }
}

// ─── Full Recalculation ─────────────────────────────────────────────────────

export interface RecalcResult {
  holdings: Holding[]
  events: LiquidityEvent[]
  loans: Loan[]
  corrections: number
}

/**
 * Full reconciliation of all balances and active states.
 *
 * 1. Sync active states: for each RE holding, ensure linked events/loans match
 * 2. For each funded event, check if deductionApplied matches what it should be
 * 3. Apply corrections (restore or deduct) only where there's a mismatch
 *
 * This is idempotent — running it multiple times produces the same result.
 */
export function recalculateBalances(
  holdings: Holding[],
  events: LiquidityEvent[],
  loans: Loan[],
): RecalcResult {
  const now = new Date().toISOString()
  let newHoldings = holdings.map((h) => ({ ...h }))
  let newEvents = events.map((e) => ({ ...e }))
  let newLoans = loans.map((l) => ({ ...l }))
  let corrections = 0

  // ── Step 1: Sync active states ──
  const reHoldings = newHoldings.filter((h) => h.isRealEstate)
  for (const reHolding of reHoldings) {
    const reActive = reHolding.isActive ?? true
    const linkedIds = collectLinkedEventIds(
      reHolding,
      newLoans as Loan[],
      newEvents,
    )

    for (const eid of linkedIds) {
      const idx = newEvents.findIndex((e) => e.id === eid)
      if (idx !== -1 && (newEvents[idx].isActive ?? true) !== reActive) {
        newEvents[idx] = { ...newEvents[idx], isActive: reActive }
        corrections++
      }
    }

    if (reHolding.linkedLoanId) {
      const loanIdx = newLoans.findIndex((l) => l.id === reHolding.linkedLoanId)
      if (loanIdx !== -1 && newLoans[loanIdx].isActive !== reActive) {
        newLoans[loanIdx] = { ...newLoans[loanIdx], isActive: reActive }
        corrections++
      }
    }
  }

  // ── Step 2: Reconcile funded event deductions ──
  // For each funded event, determine if deduction SHOULD be applied (event is active)
  // and compare with whether it IS currently applied (deductionApplied flag)
  const allFundedEvents = newEvents.filter(
    (e) => e.fundedFromHoldingId && e.startYear === 0 && e.amount < 0,
  )

  for (const evt of allFundedEvents) {
    const shouldBeApplied = evt.isActive ?? true
    const isCurrentlyApplied = evt.deductionApplied ?? true // legacy events assumed applied
    const amount = Math.abs(evt.amount)
    const fundingId = evt.fundedFromHoldingId!

    if (shouldBeApplied && !isCurrentlyApplied) {
      // Should be deducted but isn't → deduct now
      const hIdx = newHoldings.findIndex((h) => h.id === fundingId)
      if (hIdx !== -1) {
        newHoldings[hIdx] = {
          ...newHoldings[hIdx],
          amount: Math.max(0, newHoldings[hIdx].amount - amount),
        }
      }
      // Update the flag
      const eIdx = newEvents.findIndex((e) => e.id === evt.id)
      if (eIdx !== -1) {
        newEvents[eIdx] = { ...newEvents[eIdx], deductionApplied: true }
      }
      corrections++
    } else if (!shouldBeApplied && isCurrentlyApplied) {
      // Should NOT be deducted but is → restore
      const hIdx = newHoldings.findIndex((h) => h.id === fundingId)
      if (hIdx !== -1) {
        newHoldings[hIdx] = {
          ...newHoldings[hIdx],
          amount: newHoldings[hIdx].amount + amount,
        }
      }
      // Update the flag
      const eIdx = newEvents.findIndex((e) => e.id === evt.id)
      if (eIdx !== -1) {
        newEvents[eIdx] = { ...newEvents[eIdx], deductionApplied: false }
      }
      corrections++
    }
    // If shouldBeApplied === isCurrentlyApplied → no action needed (idempotent!)
  }

  // ── Step 3: Record history for any holdings whose amounts changed ──
  for (let i = 0; i < newHoldings.length; i++) {
    const original = holdings.find((h) => h.id === newHoldings[i].id)
    if (original && original.amount !== newHoldings[i].amount) {
      const currentHistory: HoldingHistoryEntry[] = newHoldings[i].history || [
        { amount: original.amount, date: original.createdAt || now },
      ]
      newHoldings[i] = {
        ...newHoldings[i],
        history: [
          ...currentHistory,
          { amount: newHoldings[i].amount, date: now },
        ],
      }
    }
  }

  return {
    holdings: newHoldings,
    events: newEvents,
    loans: newLoans,
    corrections,
  }
}
